import type { Debt } from '@/types'
import { effectiveAPR } from './promoWatcher'
import { isPaidOff } from './paidOff'

export function monthlyInterest(balance: number, apr: number): number {
  return balance * (apr / 100 / 12)
}

/**
 * Weighted average APR across optimization-tracked debts.
 *
 * Uses the EFFECTIVE APR (post-promo, post-BT) per debt — pre-v0.7.14 this
 * used `debt.apr` (base) which disagreed with how every other surface in the
 * app weights APR. Same drift shape as the v0.7.2 DebtBreakdownPie fix.
 *
 * Invariant: the value here should equal what the user reads off the Strategy
 * Engine's effective-APR list weighted the same way. Pinned by
 * cross-surface tests in `tests/unit/invariants/cross-surface.test.ts`.
 */
export function weightedAverageAPR(debts: Debt[]): number {
  const now = new Date()
  const optimized = debts.filter((d) => d.isInOptimizationEngine && d.balance > 0)
  if (optimized.length === 0) return 0
  const totalBalance = optimized.reduce((sum, d) => sum + d.balance, 0)
  if (totalBalance === 0) return 0
  const weightedSum = optimized.reduce((sum, d) => sum + effectiveAPR(d, 0, now) * d.balance, 0)
  return weightedSum / totalBalance
}

/**
 * Total monthly interest burn across the portfolio.
 *
 * Uses the EFFECTIVE APR (post-promo, post-BT) per debt — pre-v0.7.14 this
 * used `debt.apr` (base) which disagreed with `TopInterestBurners` (which
 * already used effective APR). Two cards on the same Dashboard reported
 * different numbers; this aligns them.
 *
 * Pinned by cross-surface test that asserts this fn agrees with summing
 * `monthlyInterest(d.balance, effectiveAPR(...))` across the same debts.
 */
export function totalMonthlyInterestBurn(debts: Debt[]): number {
  const now = new Date()
  return debts
    .filter((d) => d.isInOptimizationEngine && d.balance > 0)
    .reduce((sum, d) => sum + monthlyInterest(d.balance, effectiveAPR(d, 0, now)), 0)
}

/**
 * Sum of registry-default minimum payments for optimization-tracked debts.
 *
 * Note: this is the REGISTRY-LEVEL total (Dashboard view) — it does NOT
 * honor `monthMinimumPaymentOverride` since overrides are per-month-entry
 * concepts. For active-month minimums (which honor overrides), use
 * `computeLedgerTotals(...).survivalFloor − resolvedEssentials` instead.
 * See `docs/architecture/calculation-invariants.md` for the full mapping.
 *
 * v0.8.57: paid-off debts are excluded. Mark Paid Off zeroes the balance
 * but keeps `minimumPayment`, so the Dashboard was adding every cleared
 * debt's old minimum — disagreeing with the Strategy Engine budget, the
 * Extra Payment slider and the Registry.
 */
export function totalMinimumPayments(debts: Debt[]): number {
  return debts
    .filter((d) => d.isInOptimizationEngine && !isPaidOff(d))
    .reduce((sum, d) => sum + d.minimumPayment, 0)
}
